// namespace Pacem {
import { NullChecker } from "./null-checker";
/**
 * Computes the Longest Common Subsequence (LCS) table for two arrays of strings.
 * @param aTokens Array of strings from the first input.
 * @param bTokens Array of strings from the second input.
 * @returns The DP table for LCS lengths.
 */
function lcs(aTokens, bTokens) {
    const m = aTokens.length;
    const n = bTokens.length;
    // Initialize DP table with zeros
    const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    // Fill DP table
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (aTokens[i - 1] === bTokens[j - 1]) {
                // Tokens match, increment LCS length
                dp[i][j] = dp[i - 1][j - 1] + 1;
            }
            else {
                // Tokens don't match, take max length from previous states
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }
    return dp;
}
/**
 * Internal diff implementation using backtracking on the LCS DP table.
 * Works on token arrays (characters, words, lines).
 * @param aTokens Array of strings from the first input.
 * @param bTokens Array of strings from the second input.
 * @param dp The precomputed LCS DP table.
 * @param joiner The string to use when joining tokens of the same type (e.g., "" for chars, "\n" for lines).
 * For word diffing, joiner is effectively "" because whitespace is part of the tokens.
 * @returns An array of DiffTokens representing the differences.
 */
function diffInternal(aTokens, bTokens, dp, joiner = '') {
    const m = aTokens.length;
    const n = bTokens.length;
    const result = [];
    let i = m, j = n;
    let currentToken = null;
    const finalizeToken = () => {
        if (currentToken) {
            result.push(currentToken);
            currentToken = null;
        }
    };
    // Backtrack through the DP table
    while (i > 0 || j > 0) { // Continue until both sequences are fully processed
        var currentType = null;
        let currentValue = null;
        if (i > 0 && j > 0 && aTokens[i - 1] === bTokens[j - 1]) {
            // Common token
            currentType = '=';
            currentValue = aTokens[i - 1];
            i--;
            j--;
        }
        else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            // Token added in b
            currentType = '+';
            currentValue = bTokens[j - 1];
            j--;
        }
        else if (i > 0 && (j === 0 || dp[i - 1][j] > dp[i][j - 1])) {
            // Token removed from a
            currentType = '-';
            currentValue = aTokens[i - 1];
            i--;
        }
        if (currentType && currentValue !== null) {
            if (currentToken && currentToken.type === currentType) {
                // Prepend the value to the existing token
                currentToken.value = currentValue + (currentToken.value ? joiner : '') + currentToken.value;
            }
            else {
                // Finalize the previous token (if different type) and start a new one
                finalizeToken();
                currentToken = { type: currentType, value: currentValue };
            }
        }
        else {
            // Should not happen in standard LCS backtracking, but finalize just in case
            finalizeToken();
        }
    }
    finalizeToken(); // Add the last accumulated token
    return result.reverse(); // Reverse to get the correct order
}
/**
* Performs character-level diffing.
* @param a The first string.
* @param b The second string.
* @returns An array of DiffTokens.
*/
function diffChars(a, b) {
    const aTokens = a.split(''); // Split into characters
    const bTokens = b.split(''); // Split into characters
    const dp = lcs(aTokens, bTokens);
    // No joiner needed as characters are concatenated directly
    return diffInternal(aTokens, bTokens, dp, '');
}
/**
 * Performs word-level diffing, preserving whitespace and line breaks.
 * It tokenizes based on sequences of non-whitespace (\S+) or whitespace (\s+).
 * @param a The first string.
 * @param b The second string.
 * @returns An array of DiffTokens.
 */
function diffWords(a, b) {
    // Tokenize into words (\S+) and whitespace/newlines (\s+)
    // This preserves the original spacing and line breaks.
    const wordRegex = /(\S+|\s+)/g;
    const aTokens = a.match(wordRegex) || [];
    const bTokens = b.match(wordRegex) || [];
    const dp = lcs(aTokens, bTokens);
    // No joiner needed; whitespace tokens handle spacing.
    return diffInternal(aTokens, bTokens, dp, '');
}
/**
 * Performs line-level diffing.
 * @param a The first string.
 * @param b The second string.
 * @returns An array of DiffTokens where each token's value can contain multiple lines.
 */
function diffLines(a, b) {
    // Split by newline characters, keeping the EOL character concept separate
    // The result of split does not contain the separators themselves.
    const lineRegex = /\r?\n/;
    const aTokens = a.split(lineRegex);
    const bTokens = b.split(lineRegex);
    // Handle potential empty string added by split if text ends with a newline
    // (This behavior is often desired, representing an empty line after the last newline)
    // Example: "a\nb\n".split(/\n/) -> ["a", "b", ""]
    const dp = lcs(aTokens, bTokens);
    // Join consecutive lines of the same type with newline characters
    return diffInternal(aTokens, bTokens, dp, '\n');
}
//#endregion
export class Strings {
    /** @deprecated Use String.prototype.padStart() instead. */
    static leftPad(v, targetLength, padChar) {
        let retval = v.toString();
        return retval.padStart(targetLength, padChar);
        //while (retval.length < targetLength)
        //    retval = padChar + retval;
        //return retval;
    }
    static format(template, ...args) {
        const array = Array.from(args);
        let retval = template;
        for (let j = 0; j < array.length; j++) {
            const regex = new RegExp('(^|[^\\{])\\{' + j + '\\}($|[^\\}])', 'g');
            retval = retval.replaceAll(regex, (match, ...args0) => {
                return match.replace('{' + j + '}', array[j]);
            });
        }
        const check = /(^|[^\{}])\{(\d+)\}($|[^\}])/.exec(retval);
        if (!NullChecker.isNullOrEmpty(check)) {
            throw new Error(`Missing argument at index ${check[2]}.`);
        }
        return retval;
    }
    static diff(a, b, mode = 'char') {
        switch (mode) {
            case 'line':
                return diffLines(a, b);
            case 'word':
                return diffWords(a, b);
            default:
                return diffChars(a, b);
        }
    }
    static async hash(input, algo = 'SHA-256') {
        const hashBuffer = await crypto.subtle.digest(algo, new TextEncoder().encode(input));
        return Array.from(new Uint8Array(hashBuffer))
            .map(byte => byte.toString(16).padStart(2, '0'))
            .join('');
    }
}
// }
